// components/reports/ActiveCoursesReport.tsx
import React, { useState } from "react";
import {
  Card,
  Col,
  Row,
  Statistic,
  Table,
  Spin,
  Alert,
  Space,
  Typography,
  Tag,
  Progress,
  Flex,
  Button,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  TrophyOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useQuery } from "react-query";
import {
  customerReportService,
  type ReportParams,
} from "../../services/customerReportService";
import moment from "moment";
import { exportCoursesUseReportToExcel } from "../../utils/excelExport";

const { Text } = Typography;

interface ActiveCoursesReportProps {
  params: ReportParams;
}

const ActiveCoursesReport: React.FC<ActiveCoursesReportProps> = ({
  params,
}) => {
  const [exportLoading, setExportLoading] = useState(false);

  const { data, isLoading, error } = useQuery(
    ["active-courses-report", params],
    () => customerReportService.getActiveCoursesReport(params),
    {
      enabled: !!params.startDate && !!params.endDate,
      staleTime: 5 * 60 * 1000,
    }
  );

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="ເກີດຂໍ້ຜິດພາດ"
        description="ບໍ່ສາມາດດຶງຂໍ້ມູນລາຍງານຄອສກຳລັງໃຊ້ງານໄດ້"
        type="error"
        showIcon
      />
    );
  }

  const reportData = data?.data;

  if (!reportData) {
    return (
      <Alert
        message="ບໍ່ມີຂໍ້ມູນ"
        description="ບໍ່ພົບຂໍ້ມູນລາຍງານໃນຊ່ວງເວລາທີ່ເລືອກ"
        type="info"
        showIcon
      />
    );
  }

  // Columns สำหรับ Active Courses
  const activeCoursesColumns = [
    {
      title: "ລູກຄ້າ",
      dataIndex: ["customer", "name"],
      key: "customer",
    },
    {
      title: "ຄອສ",
      dataIndex: ["course", "name"],
      key: "course",
    },
    {
      title: "Sessions",
      key: "sessions",
      render: (record: any) => (
        <Space direction="vertical" size={0}>
          <Text>{`${record.usedSessions}/${record.totalSessions}`}</Text>
          <Progress
            percent={Math.round(
              (record.usedSessions / record.totalSessions) * 100
            )}
            size="small"
            status={record.remainingSessions === 0 ? "exception" : "active"}
          />
        </Space>
      ),
    },
    {
      title: "ວັນໝົດອາຍຸ",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (date: string) => {
        const daysLeft = moment(date).diff(moment(), "days");
        return (
          <Space>
            <Text>{moment(date).format("DD/MM/YYYY")}</Text>
            {daysLeft <= 7 && <Tag color="red">ໃກ້ໝົດອາຍຸ</Tag>}
          </Space>
        );
      },
    },
  ];

  // Columns สำหรับ Expiring Courses
  const expiringCoursesColumns = [
    {
      title: "ລູກຄ້າ",
      dataIndex: ["customer", "name"],
      key: "customer",
    },
    {
      title: "ເບີໂທ",
      dataIndex: ["customer", "phone"],
      key: "phone",
    },
    {
      title: "ຄອສ",
      dataIndex: ["course", "name"],
      key: "course",
    },
    {
      title: "ວັນໝົດອາຍຸ",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (date: string) => {
        const daysLeft = moment(date).diff(moment(), "days");
        return (
          <Space>
            <WarningOutlined style={{ color: "#faad14" }} />
            <Text>{moment(date).format("DD/MM/YYYY")}</Text>
            <Tag color="orange">{daysLeft} ວັນ</Tag>
          </Space>
        );
      },
    },
  ];

  // ฟังก์ชัน Export
  const handleExportToExcel = async () => {
    setExportLoading(true);
    try {
      const dateRangeInfo = {
        startDate: params.startDate,
        endDate: params.endDate,
      };

      // ✅ แก้ไขตรงนี้
      if (reportData?.activeCourses) {
        exportCoursesUseReportToExcel(reportData?.activeCourses, dateRangeInfo);
        message.success("ສົ່ງອອກລາຍງານຄອສສຳເລັດ");
      } else {
        message.error("ບໍ່ມີຂໍ້ມູນສຳລັບສົ່ງອອກ");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("ເກີດຂໍ້ຜິດພາດໃນການສົ່ງອອກ");
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Statistics Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ຄອສທີ່ໃຊ້ງານ
                </Text>
              }
              value={reportData?.summary?.totalActiveCourses}
              suffix="ຄອສ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  Sessions ລວມ
                </Text>
              }
              value={reportData?.summary?.totalSessions}
              suffix="ຄັ້ງ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  Sessions ໃຊ້ແລ້ວ
                </Text>
              }
              value={reportData?.summary?.usedSessions}
              suffix="ຄັ້ງ"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card
            bordered={false}
            style={{
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              borderRadius: 12,
            }}
          >
            <Statistic
              title={
                <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                  ອັດຕາການໃຊ້
                </Text>
              }
              value={reportData?.summary?.usagePercentage}
              suffix="%"
              valueStyle={{ color: "#fff", fontSize: 24 }}
              prefix={<TrophyOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Active Courses Table */}
      <Card
        title={
          <Flex justify="space-between" align="center">
            <Space>
              <CheckCircleOutlined style={{ color: "#667eea" }} />
              <Text strong>ຄອສທີ່ກຳລັງໃຊ້ງານ</Text>
            </Space>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportToExcel}
              loading={exportLoading}
              style={{
                background: "linear-gradient(135deg, #52c41a 0%, #389e0d 100%)",
                border: "none",
              }}
            >
              ສົ່ງອອກ Excel
            </Button>
          </Flex>
        }
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <Table
          dataSource={reportData?.activeCourses}
          columns={activeCoursesColumns}
          pagination={{ pageSize: 10 }}
          rowKey="_id"
        />
      </Card>

      {/* Expiring Courses Table */}
      <Card
        title={
          <Space>
            <WarningOutlined style={{ color: "#faad14" }} />
            <Text strong>ຄອສທີ່ໃກ້ໝົດອາຍຸ (30 ວັນ)</Text>
          </Space>
        }
        bordered={false}
        style={{ borderRadius: 12, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
      >
        <Table
          dataSource={reportData?.expiringCourses}
          columns={expiringCoursesColumns}
          pagination={false}
          rowKey="_id"
        />
      </Card>
    </Space>
  );
};

export default ActiveCoursesReport;
